import { getApiClient } from './api-client';
import { SCHEDULE_SERVICE_URL as servicePath } from '../constants/APIconstants';
import { CHECK_IN_METHODS, SCHEDULE_EVENT_TYPES } from '../constants/schedule.constants';

class ScheduleServiceAPI {
  async getScheduleEvents({
    userId,
    scheduleEventGroupIds,
    eventType,
    startTime,
    endTime,
    startsBefore,
    sortOrder,
    page = 1,
    pageSize,
  } = {}) {
    const params = new URLSearchParams();

    if (userId) params.append('userId', userId);
    if (scheduleEventGroupIds?.length > 0) {
      scheduleEventGroupIds.forEach(id => params.append('scheduleEventGroupId', id));
    }
    if (eventType?.length > 0) {
      eventType.forEach(t => params.append('eventType', t));
    }
    if (startTime) params.append('startTime', startTime);
    if (endTime) params.append('endTime', endTime);
    if (startsBefore) params.append('startsBefore', startsBefore);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (pageSize) params.append('pageSize', pageSize.toString());

    params.append('page', page.toString());

    const { scheduleEvents, ...response } = await getApiClient().get(
      `${servicePath}events?${params.toString()}`,
    );

    return { events: scheduleEvents, ...response };
  }

  /**
   * @param {{ id: string, includeChildren?: boolean }} params
   *   `includeChildren` adds the live CASE `children[]` to a parent event.
   */
  async getScheduleEvent({ id, includeChildren = false }) {
    const params = new URLSearchParams();
    if (includeChildren) params.append('includeChildren', 'true');
    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : '';
    return getApiClient().get(`${servicePath}events/${id}${suffix}`);
  }

  async importSchedule({
    scheduleEventGroups,
    scheduleEvents,
    overrideEvents,
    skipEmail = true,
    createUserIfNotExists = false,
    fileName,
    sheets,
  }) {
    return getApiClient().post(servicePath, {
      scheduleEventGroups,
      scheduleEvents,
      overrideEvents,
      skipEmail,
      createUserIfNotExists,
      fileName,
      sheets,
    });
  }

  async getEventGroups({ type } = {}) {
    const params = new URLSearchParams();
    if (type) params.append('type', type);

    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const { eventGroups } = await getApiClient().get(`${servicePath}event-groups${suffix}`);
    return { eventGroups };
  }

  // Recurring events are a ScheduleEventGroup that materializes occurrences.
  // Create/edit/delete a whole series via these; single-occurrence changes stay
  // on the event endpoints (createEducationalEvent / patchEvent / deleteEvent).
  async createEventGroup(body) {
    return getApiClient().post(`${servicePath}event-groups`, body);
  }

  async updateEventGroup({ groupId, patch }) {
    return getApiClient().patch(`${servicePath}event-groups/${groupId}`, patch);
  }

  async deleteEventGroup({ groupId }) {
    return getApiClient().delete(`${servicePath}event-groups/${groupId}`);
  }

  async addEventGroupResources({ groupId, resources }) {
    return getApiClient().post(`${servicePath}event-groups/${groupId}/resources`, resources);
  }

  async removeEventGroupResource({ groupId, resourceId }) {
    return getApiClient().delete(`${servicePath}event-groups/${groupId}/resources/${resourceId}`);
  }

  async createEvent({ title, scheduleEventGroupId, attendeeIds, startTime, endTime, isAllDay }) {
    return getApiClient().post(`${servicePath}events`, {
      title,
      scheduleEventGroupId,
      attendeeIds,
      startTime,
      endTime,
      isAllDay,
    });
  }

  async createEducationalEvent({ title, location, tag, startTime, endTime, isAllDay, attributes }) {
    return getApiClient().post(`${servicePath}events`, {
      eventType: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      title,
      location,
      tag,
      startTime,
      endTime,
      isAllDay,
      attributes,
    });
  }

  async patchEvent({ eventId, patch }) {
    return getApiClient().patch(`${servicePath}events/${eventId}`, patch);
  }

  async deleteEvent({ eventId }) {
    return getApiClient().delete(`${servicePath}events/${eventId}`);
  }

  async addEventAttendees({ eventId, attendeeIds }) {
    return getApiClient().post(`${servicePath}events/${eventId}/attendees`, {
      attendeeIds,
    });
  }

  async removeEventAttendees({ eventId, attendeeIds }) {
    return getApiClient().delete(`${servicePath}events/${eventId}/attendees`, {
      data: { attendeeIds },
    });
  }

  async addEventResources({ eventId, resources }) {
    return getApiClient().post(`${servicePath}events/${eventId}/resources`, resources);
  }

  async removeEventResource({ eventId, resourceId }) {
    return getApiClient().delete(`${servicePath}events/${eventId}/resources/${resourceId}`);
  }

  async getScheduleFilters() {
    // Backend returns { items: [...] }
    const { items } = await getApiClient().get(`${servicePath}filters`);
    return { filters: items || [] };
  }

  async openCheckInSession({ eventId }) {
    return getApiClient().put(`${servicePath}events/${eventId}/check-ins/session`);
  }

  async getCheckInSession({ eventId }) {
    return getApiClient().get(`${servicePath}events/${eventId}/check-ins/session`);
  }

  async closeCheckInSession({ eventId }) {
    return getApiClient().delete(`${servicePath}events/${eventId}/check-ins/session`);
  }

  async getCheckIns({ eventId, attendanceMode, method, userId, limit, cursor } = {}) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (cursor) params.append('cursor', cursor);
    if (attendanceMode) params.append('attendanceMode', attendanceMode);
    if (method) params.append('method', method);
    if (userId) params.append('userId', userId);

    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : '';
    return getApiClient().get(`${servicePath}events/${eventId}/check-ins${suffix}`);
  }

  // { checkedIn, checkIn: { id, checkedInAt, method, attendanceMode } | null }
  async getMyCheckIn({ eventId }) {
    return getApiClient().get(`${servicePath}events/${eventId}/check-ins/me`);
  }

  async manualCheckIn({ eventId, userId, attendanceMode }) {
    return getApiClient().post(`${servicePath}events/${eventId}/check-ins`, {
      method: CHECK_IN_METHODS.MANUAL,
      attendanceMode,
      userId,
    });
  }

  async selfCheckIn({ eventId, code, attendanceMode }) {
    return getApiClient().post(`${servicePath}events/${eventId}/check-ins`, {
      method: CHECK_IN_METHODS.QR,
      attendanceMode,
      ...(code && { code }),
    });
  }

  async selfAttest({ eventId, attendanceMode }) {
    return getApiClient().post(`${servicePath}events/${eventId}/check-ins`, {
      method: CHECK_IN_METHODS.SELF_ATTESTATION,
      attendanceMode,
    });
  }

  // ── Attendance reports ──────────────────────────────────────────────────

  _buildReportParams({ from, until } = {}) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (until) params.append('until', until);
    return params;
  }

  async getAttendanceReportByPerson({ from, until } = {}) {
    const query = this._buildReportParams({ from, until }).toString();
    const suffix = query ? `?${query}` : '';
    return getApiClient().get(`${servicePath}attendance-reports/people${suffix}`);
  }

  async getPersonAttendance({ userId, from, until } = {}) {
    const query = this._buildReportParams({ from, until }).toString();
    const suffix = query ? `?${query}` : '';
    return getApiClient().get(`${servicePath}attendance-reports/people/${userId}${suffix}`);
  }

  async getAttendanceReportByEvent({ from, until } = {}) {
    const query = this._buildReportParams({ from, until }).toString();
    const suffix = query ? `?${query}` : '';
    return getApiClient().get(`${servicePath}attendance-reports/events${suffix}`);
  }

  /**
   * Download a backend-generated XLSX report as a Blob. `responseType: 'blob'`
   * preserves the binary body; the file name is built client-side by the caller.
   * @param {string} path relative to the schedule service path
   * @returns {Promise<{ blob: Blob }>}
   */
  async _downloadReport(path) {
    const blob = await getApiClient().get(`${servicePath}${path}`, { responseType: 'blob' });
    // responseType 'blob' makes axios resolve any 2xx, so a JSON error body or
    // empty payload would otherwise be saved as a valid-looking .xlsx.
    const isSpreadsheet = /spreadsheetml|excel|octet-stream/.test(blob?.type || '');
    if (!blob || blob.size === 0 || !isSpreadsheet) {
      throw new Error(
        `Attendance report download returned a non-spreadsheet payload (type="${blob?.type ?? ''}", size=${blob?.size ?? 0})`,
      );
    }
    return { blob };
  }

  exportPersonReport({ userId, from, until } = {}) {
    const query = this._buildReportParams({ from, until }).toString();
    const suffix = query ? `?${query}` : '';
    return this._downloadReport(`attendance-reports/people/${userId}/export${suffix}`);
  }

  exportEventReport({ eventId } = {}) {
    return this._downloadReport(`attendance-reports/events/${eventId}/export`);
  }

  exportPeopleReportBulk({ from, until } = {}) {
    const query = this._buildReportParams({ from, until }).toString();
    const suffix = query ? `?${query}` : '';
    return this._downloadReport(`attendance-reports/people/exports${suffix}`);
  }

  exportEventsReportBulk({ from, until } = {}) {
    const query = this._buildReportParams({ from, until }).toString();
    const suffix = query ? `?${query}` : '';
    return this._downloadReport(`attendance-reports/events/exports${suffix}`);
  }
}

export default new ScheduleServiceAPI();
